'use client'

import { cn } from '../../utils/cn'
import type { StepWizardProps } from './types'

export function StepWizard({ steps, currentStep, onStepClick, orientation = 'horizontal', className }: StepWizardProps) {
  const progress = steps.filter(s => s.status === 'completed').length / steps.length * 100

  if (orientation === 'vertical') {
    return (
      <div className={cn('space-y-0', className)}>
        {steps.map((step, i) => (
          <div key={step.id} className="flex gap-4">
            {/* Connector + dot */}
            <div className="flex flex-col items-center">
              <button
                onClick={() => onStepClick?.(i)}
                disabled={!onStepClick}
                className={cn(
                  'flex items-center justify-center w-8 h-8 rounded-full border-2 text-xs font-bold transition-all shrink-0',
                  step.status === 'completed' && 'bg-primary/20 border-primary text-primary',
                  step.status === 'active' && 'bg-primary border-primary text-primary-foreground',
                  step.status === 'error' && 'bg-destructive/20 border-destructive text-destructive',
                  step.status === 'pending' && 'border-border text-muted-foreground',
                )}
              >
                {step.status === 'completed' ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                ) : step.status === 'error' ? '!' : i + 1}
              </button>
              {i < steps.length - 1 && (
                <div className={cn('w-0.5 h-12 my-1', step.status === 'completed' ? 'bg-primary/40' : 'bg-border')} />
              )}
            </div>
            {/* Content */}
            <div className="pb-8 pt-1">
              <h4 className={cn('text-sm font-semibold', step.status === 'active' ? 'text-foreground' : 'text-muted-foreground')}>
                {step.label}
              </h4>
              {step.description && <p className="text-xs text-muted-foreground mt-0.5">{step.description}</p>}
            </div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className={cn('space-y-4', className)}>
      {/* Progress bar */}
      <div className="h-1 rounded-full bg-border overflow-hidden">
        <div className="h-full rounded-full bg-primary transition-all duration-500 ease-out" style={{ width: `${progress}%` }} />
      </div>

      {/* Steps row */}
      <div className="flex items-center justify-between">
        {steps.map((step, i) => (
          <div key={step.id} className="flex items-center gap-2 flex-1">
            <button
              onClick={() => onStepClick?.(i)}
              disabled={!onStepClick}
              className={cn(
                'flex items-center justify-center w-8 h-8 rounded-full border-2 text-xs font-bold transition-all shrink-0',
                step.status === 'completed' && 'bg-primary/20 border-primary text-primary',
                step.status === 'active' && 'bg-primary border-primary text-primary-foreground',
                step.status === 'error' && 'bg-destructive/20 border-destructive text-destructive',
                step.status === 'pending' && 'border-border text-muted-foreground',
              )}
            >
              {step.status === 'completed' ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
              ) : step.status === 'error' ? '!' : i + 1}
            </button>
            <span className={cn('text-xs font-medium hidden sm:block', step.status === 'active' ? 'text-foreground' : 'text-muted-foreground')}>
              {step.label}
            </span>
            {i < steps.length - 1 && <div className={cn('flex-1 h-px mx-2', step.status === 'completed' ? 'bg-primary/40' : 'bg-border')} />}
          </div>
        ))}
      </div>
    </div>
  )
}
